/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.composable

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.collections.immutable.ImmutableList
import kotlinx.collections.immutable.toImmutableList
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import systems.frictionless.impulse.browser.composable.component.ButtonDescription
import systems.frictionless.impulse.browser.composable.component.ContentContainer
import systems.frictionless.impulse.browser.composable.component.FullScreenErrorMessage
import systems.frictionless.impulse.browser.composable.component.LoadingIndicator
import systems.frictionless.impulse.browser.composable.component.SectionHeader
import systems.frictionless.impulse.browser.composable.component.SideNavigationSection
import systems.frictionless.impulse.browser.composable.component.SurveyProgressBar
import systems.frictionless.impulse.browser.composable.component.Text
import systems.frictionless.impulse.browser.composable.component.WhiteBox
import systems.frictionless.impulse.browser.id.HtmlId
import systems.frictionless.impulse.browser.style.AppStyleSheet
import systems.frictionless.impulse.browser.ui.scrolling.IntersectionObserver
import systems.frictionless.impulse.browser.ui.scrolling.installVisibleSectionTabInteractionObserver
import systems.frictionless.impulse.browser.ui.scrolling.toSectionHeaderId
import systems.frictionless.impulse.browser.ui.scrolling.toSectionTabId
import systems.frictionless.impulse.browser.ui.scrolling.uninstallVisibleSectionTabInteractionObserver
import systems.frictionless.impulse.rest.client.model.Progress
import systems.frictionless.impulse.rest.client.model.RestResponse
import systems.frictionless.impulse.rest.model.Insight
import systems.frictionless.impulse.viewmodel.InsightsViewModel
import systems.frictionless.impulse.viewmodel.NetworkViewModelState

/** Calls javascript to scroll to div
 * @param idOfDivToBeShown id of the DIV element to be shown
 */
fun scrollToDiv(idOfDivToBeShown: String) {
    val element = document.getElementById(idOfDivToBeShown)

    requireNotNull(element) { "Unable to find div with id $idOfDivToBeShown" }

    element.scrollIntoView(js("({behavior:'smooth'})"))
}

@Composable
fun InsightsSection(
    viewModel: InsightsViewModel,
    @Suppress("UNUSED_PARAMETER") action: () -> Unit
) {
    val composableScope = rememberCoroutineScope()

    ContentContainer(
        navigationSection = SideNavigationSection.Insights,
        mainContainerCssClass = AppStyleSheet.insights,
        mainContentContainerId = HtmlId.SectionInsights,
        mainContent = {
            when (val state = viewModel.state.collectAsState().value) {
                is NetworkViewModelState.Idle -> {
                    viewModel.loadInsights(composableScope)
                }
                is NetworkViewModelState.Busy -> {
                    LoadingIndicator()
                }
                is NetworkViewModelState.Failure -> {
                    Error(state.failure) { viewModel.loadInsights(composableScope) }
                }
                is NetworkViewModelState.Complete -> {
                    Insights(state.result)
                }
            }
        },
        progressArea = {
            SurveyProgressBar(
                progress = Progress.COMPLETE,
                nextButton =
                    ButtonDescription(
                        label = "Print",
                        enabled = true,
                        action = {
                            window.print()
                        }
                    )
            )
        }
    )
}

@Composable
private fun Error(
    errorType: RestResponse.Failure<Insight>,
    retryCallback: () -> Unit
) {
    FullScreenErrorMessage(
        "Unable to load result from the server",
        errorType
    ) { retryCallback() }
}

@Composable
private fun Insights(insights: Insight) {
    val sectionsList: List<Section> = Section.from(insights)

    WhiteBox {
        SectionHeader("Insights")

        Div(attrs = {
            classes(AppStyleSheet.tabs)
        }) {
            sectionsList.forEach { section ->
                SectionTab(section.sectionId, section.header.text)
            }
        }

        val sectionIds = sectionsList.map { it.sectionId }

        Div(attrs = {
            classes(AppStyleSheet.insightTexts)
            ref { htmlDivElement ->
                val observer: IntersectionObserver =
                    installVisibleSectionTabInteractionObserver(htmlDivElement, sectionIds)
                onDispose {
                    uninstallVisibleSectionTabInteractionObserver(observer, sectionIds)
                }
            }
        }) {
            sectionsList.forEach { section ->
                SectionContent(section)
            }
        }
    }
}

/**
 * @param content Content for the section.  It cannot contain the Header type, but subheaders are OK.
 */
data class Section(val header: Insight.Part.Header, val content: ImmutableList<Insight.Part>) {
    init {
        require(
            content.none {
                it is Insight.Part.Header
            }
        ) { "content cannot contain headers but actually contains ${content.filterIsInstance<Insight.Part.Header>()}" }
    }

    companion object {
        fun from(insight: Insight): List<Section> {
            val sectionContent = mutableListOf<Insight.Part>()
            var sectionHeader: Insight.Part.Header? = null
            val result = mutableListOf<Section>()
            insight.parts.forEach { part ->
                if (part is Insight.Part.Header) {
                    appendIfNotEmpty(result, sectionHeader, sectionContent)
                    sectionHeader = part
                    sectionContent.clear()
                } else {
                    sectionContent += part
                }
            }

            appendIfNotEmpty(result, sectionHeader, sectionContent)

            return result
        }

        private fun appendIfNotEmpty(
            result: MutableList<Section>,
            sectionHeader: Insight.Part.Header?,
            sectionContent: MutableList<Insight.Part>
        ) {
            sectionHeader?.let { existingHeader ->
                result += Section(existingHeader, sectionContent.toImmutableList())
            }
        }
    }
}

@Composable
private fun SectionTab(
    sectionId: String,
    title: String
) {
    Div(attrs = {
        id(sectionId.toSectionTabId())
        classes(AppStyleSheet.tabName)
        onClick {
            scrollToDiv(sectionId)
        }
    }) {
        Text(title)
    }
}

@Composable
private fun SectionContent(section: Section) {
    InsightsPart(section.sectionId, section.header.text) {
        section.content.forEach { part ->
            when (part) {
                is Insight.Part.Paragraph -> {
                    P {
                        Markdown(part.print())
                    }
                }
                is Insight.Part.SubHeader -> {
                    H3 {
                        Text(part.print())
                    }
                }
                is Insight.Part.Header -> {
                    unsupported("Header should not be among content parts")
                }
                is Insight.Part.Image -> {
                    unsupported("Images are not supported yet")
                }
                else -> {
                    // it would be nice to replace this else with exhaustive when checking (Kotlin 1.6 maybe?)
                    unsupported("Part ($part) is of unexpected type")
                }
            }
        }
    }
}

fun unsupported(explanation: String): Nothing {
    throw UnsupportedOperationException(explanation)
}

@Composable
fun InsightsPart(
    sectionId: String,
    title: String,
    content: @Composable () -> Unit
) {
    Div(attrs = {
        id(sectionId)
        classes(AppStyleSheet.insightsSection)
    }) {
        SectionHeader(title, sectionId.toSectionHeaderId())
        content()
    }
}

/*
 * it is expected that title is unique.
 *
 * @return ID for section instance.
 */
private val Section.sectionId: String
    get() = this.header.text
